import { CommonModule, CurrencyPipe, DatePipe, NgIf } from '@angular/common';
import { Component, inject, input, InputSignal, OnInit, forwardRef, output, } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { ImageModule } from 'primeng/image';
import { PanelModule } from 'primeng/panel';
import { ListboxModule } from 'primeng/listbox'
import { SelectButtonChangeEvent, SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from "primeng/inputnumber";
import { FloatLabelModule } from "primeng/floatlabel";
import { map, Observable, of } from 'rxjs';
import { Store } from '@ngxs/store';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { TableModule } from 'primeng/table';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Encounter } from '../../../models/encounter';
import { MedicalBill, AdjustedMedicalBill, RepricedMedicalBill, ProviderDiscount, ProviderMarkup, MarkupBasedOn } from '../../../models/medical-bill';
import { MedicalService } from '../../../services/medical.service';
import { UtilService } from '../../../services/util.service';
import { MedicalState } from '../../../state/medical/medical-state';



@Component({
  selector: 'custom-amount',
  standalone: true,
  imports: [SelectButtonModule, FormsModule, CommonModule, InputNumberModule, FloatLabelModule],
  template: `
              <div class="flex align-items-center justify-content-center w-full flex-row mb-2 gap-2">
                <div class="flex mt-2"> <p-selectButton [options]="amountTypeOptions" [allowEmpty]="false" [ngModel]="amountType" (ngModelChange)="amountTypeChanged($event)"></p-selectButton></div>
                <div class="flex mt-2">
                  <p-floatLabel>
                    <p-inputNumber id="amount" [ngModel]="amount" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="5" (ngModelChange)="amountValueChanged($event)"></p-inputNumber>
                    <label for="amount">Amount</label>
                  </p-floatLabel>
                </div>
              </div>

            `
})
export class CustomAmountComponent 
{
  amountTypeOptions=Object.values(CustomAmountType)
  // customAmount : CustomAmount = {type:CustomAmountType.Dollar,amount:0} as CustomAmount;
  amount: number = 0.00;
  amountType: CustomAmountType = CustomAmountType.Dollar;
  customAmount = input.required<CustomAmount>();
  customAmountChange = output<CustomAmount>();
  onValueChange = output<CustomAmount>();

  selectionsChanged()
  {
    //Emit that the details of this component have changed
    const amt = {type:this.amountType,amount:this.amount} as CustomAmount;
    this.customAmountChange.emit(amt);
    this.onValueChange.emit(amt)
  }

  amountTypeChanged(value:CustomAmountType)
  {
    this.amountType = value;
    this.selectionsChanged();
  }

  amountValueChanged(value : number)
  {
    this.amount = value;
    this.selectionsChanged();
  }
}

export class CustomAmount
{
  type:CustomAmountType | undefined;
  amount:number | undefined;
}

export enum CustomAmountType
{
  Dollar = "$",
  Percent = "%"
}



@Component({
  selector: 'lib-medical-bill',
  standalone: true,
  imports: [ImageModule, ButtonModule, NgIf, CurrencyPipe, CardModule, DialogModule, PanelModule,
    SelectButtonModule, FormsModule, ListboxModule, CustomAmountComponent, ButtonGroupModule, DatePipe,
    TableModule, OverlayPanelModule, TagModule, TooltipModule],
  templateUrl: './medical-bill.component.html',
  styleUrl: './medical-bill.component.scss'
})
export class MedicalBillComponent<E extends Encounter> implements OnInit
{
  medicalBill: InputSignal<MedicalBill<E>> = input.required<MedicalBill<E>>();

  potentialAdjustement: AdjustedMedicalBill<E> | undefined;
  potentialRepricing: RepricedMedicalBill<E> | undefined;

  activeAdjustment$: Observable<AdjustedMedicalBill<E> | undefined> | undefined;
  activeAdjustment: AdjustedMedicalBill<E> | undefined;

  activeRepricing$: Observable<RepricedMedicalBill<E> | undefined> | undefined;
  activeRepricing: RepricedMedicalBill<E> | undefined;

  thumbnailImage?: string;
  editorVisible: boolean = false;
  availableProviderDiscounts: ProviderDiscount[] = [];
  availableProviderMarkups: ProviderMarkup[] = [];
  selectedExistingAdjustment: ProviderDiscount | undefined;
  selectedExistingMarkup: ProviderMarkup | undefined;

  customAmount: CustomAmount = {amount: 0, type:CustomAmountType.Dollar} as CustomAmount;
  customRepricing: CustomAmount = {amount: 0, type:CustomAmountType.Dollar} as CustomAmount;

  _util: UtilService = inject(UtilService)
  _medical: MedicalService = inject(MedicalService);
  _store: Store = inject(Store);

  adjustmentOptions: string[] = ['Existing','Custom'];
  repriceOptions: string[] = ['Existing','Custom'];
  selectedAdjustmentOption: string = 'Existing';
  selectedRepriceOption: string = 'Existing';

  async ngOnInit() 
  {

    //Get the thumbnail
    var mb = this.medicalBill();
    if(mb.billThumbnail)
    {
      this.thumbnailImage = await this._util.getFileImageURL(mb.billThumbnail);
    }
    
    //Get Provider Discounts
    this.availableProviderDiscounts = await this._medical.getAvailableDiscountsForMedicalBill(mb);
    this.availableProviderMarkups = await this._medical.getAvailableMarkupsForMedicalBill(mb);


    this.activeAdjustment$ = this._store.select(MedicalState.activeAdjustment).pipe(map(f => f(mb.id)))
    this.activeAdjustment$.subscribe(a => {this.activeAdjustment = a});

    this.activeRepricing$ = this._store.select(MedicalState.activeRepricing).pipe(map(f => f(mb.id)))
    this.activeRepricing$.subscribe(r => this.activeRepricing = r);
  }

  get HEMILink() : string
  {
    return `https://hemi.1.harlowehealth.com/encounter/${this.medicalBill().case.id}`;
  }

  getTitleSection(): string
  {
    var mb = this.medicalBill();
    return `${mb.case.id} - ${mb.patient.fullName}`
  }

  getProviderDisplay() : string
  {
    var mb = this.medicalBill();
    return `${mb.servicingProvider.facility.name}`
  }

  getDetailDisplay() : string
  {
    var mb = this.medicalBill();
    return `${mb.category.categoryName}`
  }

  startEditing()
  {
    this.editorVisible = true;
  }

  deleteBill()
  {
    this._medical.deleteMedicalBill(this.medicalBill().id)
  }

  adjustmentOptionChange(event:SelectButtonChangeEvent)
  {
    if(event.value == 'Custom')
    {
      this.selectedExistingAdjustment = undefined;
    }
  }

  repriceOptionChange(event:SelectButtonChangeEvent)
  {
    if(event.value == 'Custom')
    {
      this.selectedExistingMarkup = undefined;
    }
  }

  adjustmentChanged()
  {
    if(this.selectedAdjustmentOption=='Existing' && this.selectedExistingAdjustment)
      this.calculatePotentialAdjustment();
    else if (this.selectedAdjustmentOption=='Custom')
      this.calculatePotentialAdjustment();
    else
      this.potentialAdjustement = undefined;
  }

  repricingChanged()
  {
    if(this.selectedRepriceOption=='Existing' && this.selectedExistingMarkup)
      this.calculatePotentialRepricing();
    else if (this.selectedRepriceOption=='Custom')
      this.calculatePotentialRepricing();
    else
      this.potentialRepricing = undefined;
  }

  async calculatePotentialAdjustment()
  {
    if(this.selectedExistingAdjustment != undefined)
    {
      this.potentialAdjustement = await this._medical.calculateAdjustedMedicalBill(this.medicalBill(),this.selectedExistingAdjustment);
    }
    else
    {
      this.potentialAdjustement = await this._medical.calculateManuallyAdjustedMedicalBill(this.medicalBill(),this.customAmount.type == CustomAmountType.Dollar ? this.customAmount.amount : undefined,this.customAmount.type == CustomAmountType.Percent ? (this.customAmount.amount || 0) /100 : undefined )
    }
  }

  async calculatePotentialRepricing()
  {

    if(this.activeAdjustment)
    {
      if(this.selectedExistingMarkup != undefined)
      {
        this.potentialRepricing = await this._medical.calculateRepricedMedicalBill(this.activeAdjustment, this.selectedExistingMarkup);

      }
      else
      {
        this.potentialRepricing = await this._medical.calculateManuallyRepricedMedicalBill(this.activeAdjustment, this.customRepricing.type == CustomAmountType.Dollar ? this.customRepricing.amount : undefined,this.customRepricing.type == CustomAmountType.Percent ? (this.customRepricing.amount || 0) /100 : undefined, MarkupBasedOn.AdjustedAmount);
      }
    }
  }

  confirmAdjustment()
  {
    if(this.selectedExistingAdjustment)
      this._medical.adjustMedicalBill(this.medicalBill(),this.selectedExistingAdjustment);
    else
    {
      this._medical.adjustMedicalBillManually(this.medicalBill(),this.customAmount.type == CustomAmountType.Dollar ? this.customAmount.amount : undefined,this.customAmount.type == CustomAmountType.Percent ? (this.customAmount.amount || 0) /100 : undefined );
    }
  }

  confirmRepricing()
  {
    if(this.activeAdjustment)
    {
      if(this.selectedExistingMarkup)
      {
        this._medical.repriceMedicalBill(this.activeAdjustment,this.selectedExistingMarkup);
      }
      else
      {
        this._medical.repriceMedicalBillManually(this.activeAdjustment,MarkupBasedOn.AdjustedAmount,this.customRepricing.type == CustomAmountType.Dollar ? this.customRepricing.amount : undefined,this.customRepricing.type == CustomAmountType.Percent ? (this.customRepricing.amount || 0) /100 : undefined)
      }
    }
  }

  voidAdjustment()
  {
    if(this.activeAdjustment)
      this._medical.voidAdjustment(this.activeAdjustment);
    this.potentialRepricing = undefined;
  }

  voidRepricing()
  {
    if(this.activeAdjustment && this.activeRepricing)
        this._medical.voidRepricing(this.activeAdjustment, this.activeRepricing);
  }

  lockBill()
  {
    //close the window
    this.editorVisible = false;
    if(this.activeRepricing)
      this._medical.lockRepricing(this.activeRepricing);
  }


}